import { useEffect, useRef, useState } from "react";
import { COLORS, FONTS } from "@/lib/constants";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const MAX_SECONDS = 300; // 5 minutes

export function MicButton({ onTranscribed, color, disabled, testID }) {
  const accent = color || COLORS.mirror;
  const { user } = useAuth();
  const navigate = useNavigate();
  const isLocked = false;  // Lock removed — mic always accessible; plan gates handled server-side.

  const [isRecording, setIsRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const stopTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
      try {
        mediaRef.current?.stream?.getTracks().forEach((t) => t.stop());
      } catch { /* ignore */ }
    };
  }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      alert("Voice input isn't supported in this browser.");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      alert("Microphone permission denied. Please allow mic access.");
      return;
    }
    const mime = MediaRecorder.isTypeSupported("audio/webm")
      ? "audio/webm"
      : "audio/mp4";
    const rec = new MediaRecorder(stream, { mimeType: mime });
    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size) chunksRef.current.push(e.data);
    };
    rec.onstop = async () => {
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch { /* ignore */ }
      const blob = new Blob(chunksRef.current, { type: mime });
      if (blob.size === 0) return;
      setUploading(true);
      try {
        const ext = mime.split("/")[1] || "webm";
        const res = await api.transcribeAudio({
          blob,
          filename: `recording.${ext}`,
          mime,
        });
        onTranscribed(res.text, { language: res.language });
      } catch (e) {
        alert(e?.message || "Transcription failed.");
      } finally {
        setUploading(false);
      }
    };
    rec.start();
    mediaRef.current = { rec, stream };
    setIsRecording(true);
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed((s) => s + 1);
    }, 1000);
    stopTimeoutRef.current = setTimeout(() => {
      stop();
    }, MAX_SECONDS * 1000);
  };

  const stop = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    try {
      mediaRef.current?.rec?.stop();
    } catch { /* ignore */ }
    setIsRecording(false);
  };

  const onClick = () => {
    if (uploading) return;
    if (isLocked) {
      navigate("/paywall?reason=voice");
      return;
    }
    if (isRecording) stop();
    else start();
  };

  const remaining = Math.max(0, MAX_SECONDS - elapsed);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {isRecording && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 999,
            border: `1px solid ${COLORS.border}`,
            background: COLORS.surface,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: accent,
              display: "inline-block",
              animation: "yuo-pulse 1s ease-in-out infinite",
            }}
          />
          <span
            style={{
              color: COLORS.textPrimary,
              fontFamily: FONTS.body,
              fontSize: 12,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmt(elapsed)}
            <span style={{ color: COLORS.textMuted }}> / {fmt(MAX_SECONDS)}</span>
          </span>
        </div>
      )}
      <button
        onClick={onClick}
        disabled={disabled || uploading}
        data-testid={testID || "voice-mic-button"}
        aria-label={isRecording ? "Stop recording" : "Start voice recording"}
        className={
          isRecording
            ? "yuo-mic-ripple"
            : isLocked
            ? "yuo-lock-chameleon"
            : ""
        }
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "none",
          background: isRecording
            ? `radial-gradient(38% 34% at 34% 26%, #fff, ${accent} 22%, #05060c 100%)`
            : `radial-gradient(38% 34% at 34% 26%, #fff, ${accent} 30%, #202030 65%, #05060c 100%)`,
          boxShadow: isRecording
            ? `0 0 22px ${accent}, inset -4px -6px 12px rgba(0,0,0,0.85)`
            : `0 0 14px rgba(0,0,0,0.4), inset -4px -6px 12px rgba(0,0,0,0.85)`,
          color: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 0,
          opacity: disabled || uploading ? 0.4 : 1,
          cursor: disabled || uploading ? "default" : "pointer",
          padding: 0,
        }}
      >
        {uploading ? "" : isRecording ? "" : ""}
      </button>
      {isRecording && remaining <= 20 && (
        <span style={{ color: COLORS.error, fontFamily: FONTS.body, fontSize: 11 }}>
          {remaining}s left
        </span>
      )}
    </div>
  );
}

function fmt(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}
